/**
 * Entry-point for the standalone Recommendations tab.
 *
 * • If there is exactly one active cycle  → navigate straight to /recommendations/:id
 * • If there are multiple active cycles   → show a cycle picker
 * • If there are no active cycles         → friendly empty state
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { cyclesAPI } from '../services/api';

const ROLE_LABELS = {
  hod:        { title: 'HOD Recommendations',   sub: 'Enter increment recommendations for your department.' },
  hr:         { title: 'HR Recommendations',     sub: 'Review and adjust HOD recommendations across all departments.' },
  management: { title: 'Management Approvals',   sub: 'Review HR recommendations and provide final approval.' },
  admin:      { title: 'Recommendations',        sub: 'View and manage recommendations for all roles.' },
};

export default function RecommendationsIndexPage() {
  const navigate  = useNavigate();
  const authUser  = useSelector(s => s.auth.user);
  const role      = authUser?.role || localStorage.getItem('user_role') || 'admin';
  const info      = ROLE_LABELS[role] || ROLE_LABELS.admin;

  const [cycles,  setCycles]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cyclesAPI.list()
      .then(r => {
        // Keep only active (not disabled/completed) cycles
        const active = (r.data || []).filter(c => c.is_active !== false);
        setCycles(active);
        if (active.length === 1) {
          navigate(`/recommendations/${active[0].id}`, { replace: true });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
      <span className="spinner" style={{ width: 36, height: 36 }} />
    </div>
  );

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 className="page-title">{info.title}</h1>
        <p style={{ color: 'var(--grey-400)', fontSize: 13.5, marginTop: 4 }}>{info.sub}</p>
      </div>

      {cycles.length === 0 ? (
        /* ── Empty state ── */
        <div className="card" style={{ padding: '60px 40px', textAlign: 'center' }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--grey-300)"
            strokeWidth="1.5" style={{ marginBottom: 16 }}>
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--grey-600)', margin: '0 0 6px' }}>
            No active increment cycles
          </p>
          <p style={{ fontSize: 13, color: 'var(--grey-400)', margin: 0 }}>
            Recommendations will appear here once an increment cycle is active.
          </p>
        </div>
      ) : (
        /* ── Cycle picker ── */
        <>
          <p style={{ fontSize: 13, color: 'var(--grey-500)', marginBottom: 16 }}>
            Select an increment cycle to continue:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 560 }}>
            {cycles.map(cycle => (
              <button
                key={cycle.id}
                onClick={() => navigate(`/recommendations/${cycle.id}`)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '18px 24px', background: '#fff', border: '1.5px solid var(--grey-200)',
                  borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--magenta)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--grey-200)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)' }}>
                    {cycle.cycle_id}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--grey-400)', marginTop: 3 }}>
                    {cycle.start_date} – {cycle.end_date}
                  </div>
                </div>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="var(--grey-400)" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
